import React, { useState, useEffect } from 'react';
import { apiFetch } from '../services/api_service';
import { API } from '../App';
import { Bot, AlertCircle, TrendingUp, TrendingDown, Target, CheckCircle, XCircle, Activity, Lightbulb, Users } from 'lucide-react';
import ChaiRmansCouncilModal from '../components/ChaiRmansCouncilModal';

const ESGAdvisor = () => {
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeSection, setActiveSection] = useState('overview');
  const [showCouncilModal, setShowCouncilModal] = useState(false);

  const fetchAdvisoryReport = async () => {
    const data = await apiFetch(`${API}/advisory/report`);
    if (data?._error) {
      console.error('Error fetching advisory report:', data.message);
      setLoading(false);
      return;
    }
    setReport(data);
    setLoading(false);
  };

   
  useEffect(() => {
    fetchAdvisoryReport();
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-96">
        <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-green-600 mb-4"></div>
        <p className="text-slate-600">AI Agent analyzing your ESG data...</p>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="text-center p-8">
        <AlertCircle className="mx-auto text-red-500 mb-4" size={48} />
        <p className="text-slate-600">Unable to load advisory report</p>
      </div>
    );
  }

  const { executive_summary, critical_issues, priority_kpis, strategic_recommendations, next_steps } = report;

  const getReadinessColor = (score) => {
    if (score >= 80) return { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-500' };
    if (score >= 60) return { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-500' };
    return { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-500' };
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <XCircle className="text-red-600" size={20} />;
      case 'high': return <AlertCircle className="text-orange-600" size={20} />;
      case 'medium': return <AlertCircle className="text-yellow-600" size={20} />;
      default: return <CheckCircle className="text-green-600" size={20} />;
    }
  };

  const readinessColors = getReadinessColor(executive_summary.csrd_readiness_score);

  return (
    <div className="space-y-6" data-testid="esg-advisor-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-600 to-emerald-700 rounded-lg p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot size={32} />
            <div>
              <h1 className="text-2xl font-bold">ESG Advisory Agent</h1>
              <p className="text-green-100 text-sm">AI-Powered CSRD/ESRS Compliance Analysis</p>
            </div>
          </div>
          <button
            onClick={() => setShowCouncilModal(true)}
            className="flex items-center gap-2 px-6 py-3 bg-white text-purple-700 rounded-lg font-semibold hover:bg-purple-50 transition-colors shadow-lg"
          >
            <Users size={20} />
            Chairman's Council
          </button>
        </div>
      </div>

      {/* Chairman's Council Modal */}
      <ChaiRmansCouncilModal
        isOpen={showCouncilModal}
        onClose={() => setShowCouncilModal(false)}
        esgContext={{
          compliance_score: executive_summary?.csrd_readiness_score,
          double_material_topics: report?.materiality_insights?.double_material_count,
          financial_exposure: report?.financial_summary?.total_exposure
        }}
      />

      {/* CSRD Readiness Score */}
      <div className={`bg-white rounded-lg shadow-lg border-l-4 ${readinessColors.border} p-6`}>
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-slate-900 mb-1">CSRD Readiness Assessment</h2>
            <p className="text-sm text-slate-600">Based on {executive_summary.total_indicators_analyzed} ESRS indicators</p>
          </div>
          <div className="text-center">
            <div className={`text-5xl font-bold ${readinessColors.text}`}>
              {executive_summary.csrd_readiness_score}%
            </div>
            <div className={`text-sm font-semibold ${readinessColors.text} mt-1`}>
              {executive_summary.readiness_level} Readiness
            </div>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
          <div className="text-center">
            <div className="text-2xl font-bold text-red-600">{executive_summary.critical_issues}</div>
            <div className="text-xs text-slate-600">Critical Issues</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-orange-600">{executive_summary.anomalies_detected}</div>
            <div className="text-xs text-slate-600">Anomalies Detected</div>
          </div>
          <div className="text-center">
            <div className="text-2xl font-bold text-green-600">{executive_summary.total_indicators_analyzed}</div>
            <div className="text-xs text-slate-600">Indicators Analyzed</div>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-slate-200">
          <div className="flex gap-1 p-1">
            <button
              onClick={() => setActiveSection('overview')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeSection === 'overview' 
                  ? 'bg-green-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveSection('critical')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeSection === 'critical' 
                  ? 'bg-green-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Critical Issues
            </button>
            <button
              onClick={() => setActiveSection('priorities')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeSection === 'priorities' 
                  ? 'bg-green-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Priority KPIs
            </button>
            <button
              onClick={() => setActiveSection('recommendations')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeSection === 'recommendations' 
                  ? 'bg-green-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Recommendations
            </button>
            <button
              onClick={() => setActiveSection('nextsteps')}
              className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
                activeSection === 'nextsteps' 
                  ? 'bg-green-600 text-white' 
                  : 'text-slate-600 hover:bg-slate-100'
              }`}
            >
              Next Steps
            </button>
          </div>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeSection === 'overview' && (
            <div className="space-y-6">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-4">Executive Summary</h2>
                <div className="bg-blue-50 border-l-4 border-blue-600 p-4 rounded">
                  <p className="text-sm text-blue-900">
                    Our AI agent has analyzed <strong>{executive_summary.total_indicators_analyzed} ESRS indicators</strong> across 
                    Environmental (E1-E5), Social (S1-S4), and Governance (G1) categories. Your organization shows{' '}
                    <strong className={readinessColors.text}>{executive_summary.readiness_level.toLowerCase()} readiness</strong> for 
                    CSRD compliance with a score of <strong>{executive_summary.csrd_readiness_score}%</strong>.
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <AlertCircle className="text-red-600" size={24} />
                    <h3 className="font-semibold text-red-900">Attention Required</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-red-800">
                    <li>• {executive_summary.critical_issues} critical compliance gaps identified</li>
                    <li>• {executive_summary.anomalies_detected} data anomalies detected across indicators</li>
                    <li>• Immediate action required on high-priority KPIs</li>
                  </ul>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 mb-3">
                    <Lightbulb className="text-green-600" size={24} />
                    <h3 className="font-semibold text-green-900">Key Recommendations</h3>
                  </div>
                  <ul className="space-y-2 text-sm text-green-800">
                    <li>• Focus on top 5 priority KPIs for maximum impact</li>
                    <li>• Address critical data quality issues first</li>
                    <li>• Implement systematic ESG data governance</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {/* Critical Issues Tab */}
          {activeSection === 'critical' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Critical Issues Detected</h2>
                <p className="text-sm text-slate-600 mb-4">
                  These indicators require immediate attention due to severe compliance gaps or data quality issues
                </p>
              </div>

              {critical_issues && critical_issues.length > 0 ? (
                <div className="space-y-3">
                  {critical_issues.map((issue, idx) => (
                    <div key={idx} className="bg-white border-2 border-red-300 rounded-lg p-5">
                      <div className="flex items-start justify-between mb-3">
                        <div>
                          <h3 className="font-semibold text-slate-900 text-lg">{issue.indicator}</h3>
                          <span className="text-xs font-semibold px-2 py-1 bg-red-100 text-red-800 rounded mt-1 inline-block">
                            {issue.category}
                          </span>
                        </div>
                        <span className="px-3 py-1 bg-red-600 text-white text-xs font-bold rounded-full">
                          CRITICAL
                        </span>
                      </div>

                      <div className="space-y-2">
                        {issue.issues.map((anomaly, aidx) => (
                          <div key={aidx} className="bg-red-50 border-l-4 border-red-600 p-3 rounded">
                            <div className="flex items-start gap-2">
                              {getSeverityIcon(anomaly.severity)}
                              <div className="flex-1">
                                <div className="text-sm font-semibold text-red-900 mb-1">{anomaly.description}</div>
                                <div className="text-xs text-red-700">{anomaly.impact}</div>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-green-50 rounded-lg">
                  <CheckCircle className="mx-auto text-green-600 mb-2" size={48} />
                  <p className="text-green-800 font-semibold">No critical issues detected</p>
                  <p className="text-sm text-green-600 mt-1">Your ESG indicators are in good shape!</p>
                </div>
              )}
            </div>
          )}

          {/* Priority KPIs Tab */}
          {activeSection === 'priorities' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Priority KPIs for CSRD Focus</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Ranked by urgency and impact on CSRD compliance - focus your resources here
                </p>
              </div>

              <div className="space-y-2">
                {priority_kpis && priority_kpis.slice(0, 10).map((kpi, idx) => {
                  const priorityColors = {
                    'Critical': { bg: 'bg-red-100', text: 'text-red-800', badge: 'bg-red-600' },
                    'High': { bg: 'bg-orange-100', text: 'text-orange-800', badge: 'bg-orange-600' },
                    'Medium': { bg: 'bg-yellow-100', text: 'text-yellow-800', badge: 'bg-yellow-600' },
                    'Low': { bg: 'bg-green-100', text: 'text-green-800', badge: 'bg-green-600' }
                  };
                  
                  const colors = priorityColors[kpi.priority_level];
                  
                  return (
                    <div key={idx} className={`border-2 rounded-lg p-4 ${idx < 3 ? 'border-red-400' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold text-white ${
                            idx < 3 ? 'bg-gradient-to-br from-red-500 to-orange-500' : 'bg-slate-400'
                          }`}>
                            {idx + 1}
                          </div>
                          <div>
                            <h3 className="font-semibold text-slate-900">{kpi.indicator_name}</h3>
                            <span className="text-xs text-slate-600">{kpi.indicator_code} - {kpi.category}</span>
                          </div>
                        </div>
                        <span className={`px-3 py-1 ${colors.badge} text-white text-xs font-bold rounded-full`}>
                          {kpi.priority_level}
                        </span>
                      </div>

                      {kpi.reasons && kpi.reasons.length > 0 && (
                        <div className={`mt-3 p-3 ${colors.bg} rounded`}>
                          <div className="text-xs font-semibold text-slate-700 mb-1">Why prioritized:</div>
                          <ul className="space-y-1">
                            {kpi.reasons.map((reason, ridx) => (
                              <li key={ridx} className={`text-xs ${colors.text}`}>• {reason}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Recommendations Tab */}
          {activeSection === 'recommendations' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Strategic Recommendations</h2>
                <p className="text-sm text-slate-600 mb-4">
                  AI-generated strategic guidance for improving your CSRD compliance posture
                </p>
              </div>

              <div className="space-y-3">
                {strategic_recommendations && strategic_recommendations.map((rec, idx) => (
                  <div key={idx} className="bg-blue-50 border border-blue-200 rounded-lg p-5">
                    <div className="flex items-start gap-3">
                      <Lightbulb className="text-blue-600 flex-shrink-0 mt-1" size={24} />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-semibold text-blue-900">{rec.recommendation}</h3>
                          <span className={`px-2 py-0.5 text-xs font-bold rounded ${
                            rec.priority === 'High' ? 'bg-red-600 text-white' : 'bg-blue-600 text-white'
                          }`}>
                            {rec.priority}
                          </span>
                        </div>

                        {rec.category && (
                          <div className="text-sm text-blue-700 mb-2">
                            <strong>Category:</strong> {rec.category}
                          </div>
                        )}

                        {rec.affected_indicators && (
                          <div className="text-sm text-blue-700 mb-2">
                            <strong>Affects:</strong> {rec.affected_indicators.join(', ')}
                          </div>
                        )}

                        {rec.rationale && (
                          <div className="text-sm text-blue-700 mb-2">{rec.rationale}</div>
                        )}

                        <div className="bg-white border border-blue-200 rounded p-2 mt-2">
                          <div className="text-xs font-semibold text-blue-800 mb-1">Expected Impact:</div>
                          <div className="text-xs text-blue-700">{rec.expected_impact}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Next Steps Tab */}
          {activeSection === 'nextsteps' && (
            <div className="space-y-4">
              <div>
                <h2 className="text-lg font-semibold text-slate-900 mb-2">Action Plan & Next Steps</h2>
                <p className="text-sm text-slate-600 mb-4">
                  Structured roadmap to improve CSRD compliance over the next 6 months
                </p>
              </div>

              <div className="space-y-4">
                {next_steps && next_steps.map((step, idx) => (
                  <div key={idx} className="bg-white border-2 border-green-300 rounded-lg p-5">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-10 h-10 rounded-full bg-green-600 text-white flex items-center justify-center font-bold">
                        {idx + 1}
                      </div>
                      <div>
                        <h3 className="font-semibold text-slate-900 text-lg">{step.timeframe}</h3>
                        <p className="text-sm text-slate-600">{step.action}</p>
                      </div>
                    </div>

                    <div className="bg-green-50 rounded-lg p-4">
                      <div className="text-sm font-semibold text-green-900 mb-2">Specific Tasks:</div>
                      <ul className="space-y-1">
                        {step.specific_tasks.map((task, tidx) => (
                          <li key={tidx} className="text-sm text-green-800 flex items-start gap-2">
                            <CheckCircle size={16} className="flex-shrink-0 mt-0.5" />
                            <span>{task}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-3 flex items-center gap-2 text-sm text-slate-600">
                      <Activity size={16} />
                      <span><strong>Owner:</strong> {step.owner}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ESGAdvisor;
